import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom, forkJoin } from 'rxjs';

import { AuthFacade } from '../../../modules/auth/facades';
import { ActivationChecklistInterface, GuidedTourId, GuidedTourStateInterface } from '../models';
import { contextualTourForPath } from '../registry/guided-tour.registry';
import { GuidedTourService } from './guided-tour.service';
import { OnboardingProgressApiService } from './onboarding-progress-api.service';

export const GLOBAL_TOUR_ID: GuidedTourId = 'global.v1';
const WELCOME_ROUTES = ['/dashboard'];
const SETTLE_TIMEOUT_MS = 4000;

/**
 * Elegibilidade do convite: só empresa nova (sem dados operacionais), flag ligada
 * no servidor, sem decisão prévia sobre o tour global V1 e com progresso conhecido.
 * Qualquer incerteza (null/erro) resulta em não oferecer — nunca em bloquear.
 */
export function isEligibleForWelcome(
  tours: Partial<Record<GuidedTourId, GuidedTourStateInterface>> | undefined,
  checklist: ActivationChecklistInterface | null
): boolean {
  if (!checklist?.enabled) return false;
  if (checklist.hasOperationalData !== false) return false;
  const state = tours?.[GLOBAL_TOUR_ID];
  return !state || state.status === 'not_started';
}

/**
 * Orquestra convite, replay e progresso da empresa. O estado do usuário vive no
 * backend (por vínculo usuário-empresa); aqui há apenas cache de sessão.
 */
@Injectable({ providedIn: 'root' })
export class GuidedOnboardingService {
  private readonly api = inject(OnboardingProgressApiService);
  private readonly tour = inject(GuidedTourService);
  private readonly router = inject(Router);
  private readonly authFacade = inject(AuthFacade);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _enabled = signal(false);
  private readonly _offerVisible = signal(false);
  private readonly _checklist = signal<ActivationChecklistInterface | null>(null);
  private readonly _checklistFailed = signal(false);
  private readonly currentUrl = signal(this.router.url);
  private knownCompany: string | null = null;
  private loadedFor: string | null = null;
  private loading: Promise<void> | null = null;
  private evaluatedFor: string | null = null;
  private toursSnapshot: Partial<Record<GuidedTourId, GuidedTourStateInterface>> | undefined;
  private generation = 0;

  readonly enabled = this._enabled.asReadonly();
  readonly offerVisible = this._offerVisible.asReadonly();
  readonly checklist = this._checklist.asReadonly();
  readonly checklistFailed = this._checklistFailed.asReadonly();
  readonly canReplay = computed(() => this._enabled());
  /** Tour contextual ("Conhecer esta tela") da rota atual, quando a funcionalidade está ligada. */
  readonly contextualTourId = computed(() =>
    this._enabled() ? contextualTourForPath(this.currentUrl()) : null
  );

  constructor() {
    this.knownCompany = this.authFacade.currentCompany?.companyAccountId ?? null;
    this.authFacade.company$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((company) => {
      const id = company?.companyAccountId ?? null;
      if (id === this.knownCompany) return; // reemissão da mesma empresa
      this.knownCompany = id;
      this.reset();
      // Mesma URL após a troca não gera NavigationEnd: recarrega o progresso da nova empresa.
      void this.onNavigated(this.currentUrl());
    });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.currentUrl.set(event.urlAfterRedirects));
  }

  /** Chamado a cada navegação concluída; só avalia uma vez por empresa/sessão. */
  async onNavigated(url: string): Promise<void> {
    const path = url.split('?')[0].split('#')[0];
    if (this._offerVisible() && !WELCOME_ROUTES.includes(path)) {
      this._offerVisible.set(false);
      return;
    }
    const companyAccountId = this.authFacade.currentCompany?.companyAccountId ?? null;
    if (!companyAccountId) return;
    const generation = this.generation;

    // Progresso/flag carregam uma vez por empresa, em qualquer rota (habilita a Ajuda).
    if (this.loadedFor !== companyAccountId) {
      this.loadedFor = companyAccountId;
      this.loading = (async () => {
        const [tours, checklist] = await this.load();
        if (generation !== this.generation) return; // empresa trocou: descarta resposta antiga
        this.toursSnapshot = tours?.tours;
        this._checklist.set(checklist);
        this._checklistFailed.set(checklist === null);
        this._enabled.set(!!checklist?.enabled);
      })();
    }
    await this.loading;
    if (generation !== this.generation) return;

    // O convite só é avaliado na Dashboard, uma vez por empresa/sessão.
    if (!WELCOME_ROUTES.includes(path) || this.evaluatedFor === companyAccountId) return;
    this.evaluatedFor = companyAccountId;
    if (!isEligibleForWelcome(this.toursSnapshot, this._checklist())) return;

    await this.waitUntilSettled();
    if (generation !== this.generation || this.tour.isActive()) return;
    if (!WELCOME_ROUTES.includes(this.router.url.split('?')[0])) return;
    this._offerVisible.set(true);
  }

  async refreshChecklist(): Promise<void> {
    const generation = this.generation;
    try {
      const checklist = await firstValueFrom(this.api.getCompanyChecklist());
      if (generation !== this.generation) return;
      this._checklist.set(checklist);
      this._checklistFailed.set(false);
      this._enabled.set(checklist.enabled);
    } catch {
      if (generation === this.generation) this._checklistFailed.set(true);
    }
  }

  async accept(): Promise<void> {
    this._offerVisible.set(false);
    await this.tour.start(GLOBAL_TOUR_ID);
  }

  decline(): void {
    this._offerVisible.set(false);
    this.api
      .updateTour(GLOBAL_TOUR_ID, { status: 'dismissed', version: 1 })
      .subscribe({ error: () => undefined });
  }

  /** "Conhecer o GextorPro" no menu de ajuda: replay manual, sem reescrever o estado salvo. */
  async replay(): Promise<void> {
    this._offerVisible.set(false);
    await this.tour.start(GLOBAL_TOUR_ID, { persist: false });
  }

  /** "Conhecer esta tela": inicia o tour contextual da rota atual por ação explícita. */
  async replayContextual(): Promise<void> {
    const tourId = this.contextualTourId();
    if (!tourId) return;
    this._offerVisible.set(false);
    await this.tour.start(tourId);
  }

  private reset(): void {
    this.generation += 1;
    this.evaluatedFor = null;
    this.loadedFor = null;
    this.loading = null;
    this.toursSnapshot = undefined;
    this._offerVisible.set(false);
    this._checklist.set(null);
    this._checklistFailed.set(false);
    this._enabled.set(false);
  }

  private async load() {
    try {
      return await firstValueFrom(forkJoin([this.api.getTours(), this.api.getCompanyChecklist()]));
    } catch {
      return [undefined, null] as const;
    }
  }

  /** Aguarda skeletons/carregamento da Dashboard terminarem (com teto de tempo). */
  private async waitUntilSettled(): Promise<void> {
    const deadline = Date.now() + SETTLE_TIMEOUT_MS;
    const loading = () =>
      !!document.querySelector('main [class*="skeleton"], main [aria-busy="true"]');
    await new Promise<void>((resolve) => setTimeout(resolve, 300));
    while (loading() && Date.now() < deadline) {
      await new Promise<void>((resolve) => setTimeout(resolve, 150));
    }
  }
}
